import { ThemeEvents } from '@theme/events';
import { calculateDeliveryEstimate } from '@theme/delivery-estimate';
import { isBackorderEligible, resolveAvailability } from './product-availability.js';
import { CART_STATE_ATTRIBUTE, isSampleCartDebugMode } from '@theme/beaumont-cart-state';

const STORAGE_KEY = 'beaumont-selected-state';
const STATE_CHANGE_EVENT = 'state-selector:change';

/**
 * @returns {boolean}
 */
function isDebugEnabled() {
  const value = new URLSearchParams(window.location.search).get('debug');
  return value === 'true' || value === '1';
}

/**
 * @param {string} attribute
 * @param {HTMLElement} element
 * @returns {unknown}
 */
function parseJsonAttribute(attribute, element) {
  const raw = element.getAttribute(attribute);

  if (!raw) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * @param {HTMLElement} element
 * @returns {{ etaWindows: import('@theme/delivery-estimate').EtaWindowsMap, bufferDays: number }}
 */
function getEtaPayloadFromElement(element) {
  const embedded = element.querySelector('script[type="application/json"][data-eta-windows]');

  if (embedded?.textContent) {
    try {
      const parsed = JSON.parse(embedded.textContent);
      if (parsed && typeof parsed === 'object') {
        return {
          etaWindows: /** @type {import('@theme/delivery-estimate').EtaWindowsMap} */ (
            parsed.etaWindows ?? {}
          ),
          bufferDays: Number(parsed.bufferDays) || 0,
        };
      }
    } catch {
      // Fall through.
    }
  }

  return { etaWindows: {}, bufferDays: 0 };
}

/**
 * @param {string} stateCode
 * @param {Record<string, string>} classifications
 * @param {string[]} availableStates
 * @returns {boolean}
 */
function isStateListedForProduct(stateCode, classifications, availableStates) {
  const hasClassifications = Object.keys(classifications).length > 0;
  const hasAvailableStates = availableStates.length > 0;

  if (!hasClassifications && !hasAvailableStates) {
    return false;
  }

  if (hasAvailableStates && !availableStates.includes(stateCode)) {
    return false;
  }

  if (hasClassifications && !classifications[stateCode]) {
    return false;
  }

  return true;
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function formatValue(value) {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

class PdpDebugPanel extends HTMLElement {
  /** @type {(() => void) | null} */
  #abort = null;

  /** @type {object | null} */
  #lastVariant = null;

  /** @type {string | null} */
  #cartStateAttribute = null;

  connectedCallback() {
    if (!isDebugEnabled()) {
      this.remove();
      return;
    }

    this.#ensureStyles();
    this.setAttribute('aria-hidden', 'true');
    this.style.display = 'block';

    const abortController = new AbortController();
    const { signal } = abortController;
    this.#abort = () => abortController.abort();

    document.addEventListener(STATE_CHANGE_EVENT, this.#render, { signal });
    document.addEventListener('beaumont:availability-updated', this.#render, { signal });
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    document.addEventListener('beaumont:purchase-mode-change', this.#render, { signal });
    document.addEventListener('beaumont:atc-debug', this.#render, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#refreshCartAttribute, { signal });
    window.addEventListener('storage', this.#render, { signal });

    this.#lastVariant = this.#readInitialVariant();
    this.#refreshCartAttribute();
  }

  disconnectedCallback() {
    this.#abort?.();
    this.#abort = null;
  }

  #onVariantUpdate = (event) => {
    if (event.detail.resource) {
      this.#lastVariant = event.detail.resource;
    }

    queueMicrotask(() => {
      this.#syncVariantFromDom();
      this.#render();
    });
  };

  #onVariantSelected = () => {
    queueMicrotask(() => {
      this.#syncVariantFromDom();
      this.#render();
    });
  };

  #refreshCartAttribute = async () => {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.#cartStateAttribute = cart.attributes?.[CART_STATE_ATTRIBUTE] ?? null;
    } catch {
      this.#cartStateAttribute = null;
    }

    this.#render();
  };

  /**
   * @returns {object | null}
   */
  #readInitialVariant() {
    return this.#syncVariantFromDom();
  }

  /**
   * @returns {object | null}
   */
  #syncVariantFromDom() {
    const json = document.querySelector('variant-picker script[type="application/json"]')?.textContent;

    if (!json) return this.#lastVariant;

    try {
      this.#lastVariant = JSON.parse(json);
    } catch {
      // Keep last known variant.
    }

    return this.#lastVariant;
  }

  #ensureStyles() {
    if (document.getElementById('pdp-debug-panel-styles')) return;

    const style = document.createElement('style');
    style.id = 'pdp-debug-panel-styles';
    style.textContent = `
      pdp-debug-panel {
        display: block;
        position: fixed;
        bottom: 0;
        left: 0;
        z-index: 9999;
        max-width: min(420px, 100vw);
        max-height: 50vh;
        overflow: auto;
        margin: 0;
        padding: 12px 14px;
        background: rgb(20 20 20 / 0.92);
        color: #e8e8e8;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        font-size: 11px;
        line-height: 1.45;
        border-top-right-radius: 8px;
        box-shadow: 0 -4px 24px rgb(0 0 0 / 0.35);
        pointer-events: auto;
      }

      pdp-debug-panel .pdp-debug-panel__title {
        margin: 0 0 8px;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: #9cdcfe;
      }

      pdp-debug-panel .pdp-debug-panel__atc {
        margin: 0 0 10px;
        padding: 6px 8px;
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.02em;
        border-radius: 4px;
        border: 1px solid rgb(232 232 232 / 0.25);
      }

      pdp-debug-panel .pdp-debug-panel__atc--add {
        color: #111;
        background: #b5e853;
        border-color: #b5e853;
      }

      pdp-debug-panel .pdp-debug-panel__atc--blocked {
        color: #fff;
        background: #c23b22;
        border-color: #c23b22;
      }

      pdp-debug-panel .pdp-debug-panel__atc--gated {
        color: #111;
        background: #f0c14b;
        border-color: #f0c14b;
      }

      pdp-debug-panel .pdp-debug-panel__row {
        margin: 0;
        padding: 1px 0;
        white-space: pre-wrap;
        word-break: break-word;
      }

      pdp-debug-panel .pdp-debug-panel__key {
        color: #ce9178;
      }

      pdp-debug-panel .pdp-debug-panel__section {
        margin: 10px 0 4px;
        font-weight: 700;
        color: #4ec9b0;
      }

      pdp-debug-panel .pdp-debug-panel__actions {
        margin: 0 0 8px;
      }

      pdp-debug-panel .pdp-debug-panel__button {
        font: inherit;
        color: #e8e8e8;
        background: transparent;
        border: 1px solid rgb(232 232 232 / 0.4);
        border-radius: 4px;
        padding: 4px 8px;
        cursor: pointer;
      }

      pdp-debug-panel .pdp-debug-panel__button:hover {
        background: rgb(232 232 232 / 0.08);
      }
    `;
    document.head.append(style);
  }

  #render = () => {
    this.#syncVariantFromDom();

    const availability = document.querySelector('product-availability-component');
    const stockStatus = document.querySelector('product-stock-status');
    const productForm =
      document.querySelector('product-form-component[data-cart-order-conflict="product"]') ||
      document.querySelector('product-form-component');
    const sampleForm = document.querySelector('product-form-component[data-cart-order-conflict="sample"]');
    const atcButton = productForm?.querySelector('[ref="addToCartButton"]');

    const productAtcDebug = productForm instanceof HTMLElement ? productForm.dataset.debugAtc : null;
    const productGateDebug =
      productForm instanceof HTMLElement ? productForm.dataset.debugVariantGate : null;
    const sampleAtcDebug = sampleForm instanceof HTMLElement ? sampleForm.dataset.debugAtc : null;

    /** @type {'add' | 'blocked-sample' | 'gated' | 'unknown'} */
    let atcStatusKind = 'unknown';
    let atcStatusLabel = 'ATC: unknown';

    if (productGateDebug === 'in_progress') {
      atcStatusKind = 'gated';
      atcStatusLabel = 'ATC: Gated - variant change';
    } else if (productAtcDebug === 'blocked-sample') {
      atcStatusKind = 'blocked';
      atcStatusLabel = 'ATC: Blocked - sample';
    } else if (productAtcDebug === 'add') {
      atcStatusKind = 'add';
      atcStatusLabel = 'ATC: Add';
    }

    const selectedState = sessionStorage.getItem(STORAGE_KEY);
    const classifications =
      availability instanceof HTMLElement
        ? (parseJsonAttribute('data-state-classifications', availability) ?? {})
        : {};
    const availableStates =
      availability instanceof HTMLElement
        ? (parseJsonAttribute('data-available-states', availability) ?? [])
        : [];
    const stateNames =
      availability instanceof HTMLElement ? (parseJsonAttribute('data-state-names', availability) ?? {}) : {};

    const inventoryFromDom =
      stockStatus instanceof HTMLElement
        ? Number(stockStatus.dataset.inventoryQuantity)
        : availability instanceof HTMLElement
          ? Number(availability.dataset.inventoryQuantity)
          : NaN;

    const variantInventory =
      typeof this.#lastVariant?.inventory_quantity === 'number'
        ? this.#lastVariant.inventory_quantity
        : Number.isFinite(inventoryFromDom)
          ? inventoryFromDom
          : null;

    const etaPayload =
      availability instanceof HTMLElement
        ? (() => {
            const embedded = availability.querySelector(
              'script[type="application/json"][data-eta-windows]'
            );
            if (!(embedded instanceof HTMLScriptElement)) {
              return stockStatus instanceof HTMLElement
                ? getEtaPayloadFromElement(stockStatus)
                : { etaWindows: {}, bufferDays: 0 };
            }
            try {
              const parsed = JSON.parse(embedded.textContent || '{}');
              return {
                etaWindows: parsed.etaWindows ?? {},
                bufferDays: Number(parsed.bufferDays) || 0,
              };
            } catch {
              return { etaWindows: {}, bufferDays: 0 };
            }
          })()
        : stockStatus instanceof HTMLElement
          ? getEtaPayloadFromElement(stockStatus)
          : { etaWindows: {}, bufferDays: 0 };

    const { etaWindows, bufferDays } = etaPayload;

    const availabilityResult = resolveAvailability({
      selectedState,
      classifications: /** @type {Record<string, string>} */ (classifications),
      availableStates: /** @type {string[]} */ (availableStates),
      etaWindows,
      inventoryQuantity: variantInventory,
    });

    const displayCode = selectedState
      ? /** @type {Record<string, string>} */ (classifications)[selectedState] ?? null
      : null;

    const stateListed =
      selectedState &&
      isStateListedForProduct(
        selectedState,
        /** @type {Record<string, string>} */ (classifications),
        /** @type {string[]} */ (availableStates)
      );

    const hasEtaWindows = Object.keys(etaWindows).length > 0;

    const estimate =
      selectedState && stateListed
        ? calculateDeliveryEstimate({
            etaWindows,
            bufferDays,
            stateCode: selectedState,
            displayCode,
            inventoryQuantity: variantInventory,
            sampleLabel:
              stockStatus instanceof HTMLElement ? stockStatus.dataset.sampleLabel : undefined,
          })
        : null;

    const unavailableLabel =
      stockStatus instanceof HTMLElement ? stockStatus.dataset.unavailableLabel || 'Unavailable' : 'Unavailable';

    const stateCodes =
      stockStatus instanceof HTMLElement
        ? (stockStatus.dataset.stateCodes ?? '').split(',').map((c) => c.trim()).filter(Boolean)
        : [];

    /** @type {Record<string, { display_code: string | null, listed: boolean, label: string }>} */
    const stateEstimates = {};

    for (const stateCode of stateCodes) {
      const code = /** @type {Record<string, string>} */ (classifications)[stateCode] ?? null;
      const listed = isStateListedForProduct(
        stateCode,
        /** @type {Record<string, string>} */ (classifications),
        /** @type {string[]} */ (availableStates)
      );

      const stateEstimate = listed
        ? calculateDeliveryEstimate({
            etaWindows,
            bufferDays,
            stateCode,
            displayCode: code,
            inventoryQuantity: variantInventory,
            sampleLabel:
              stockStatus instanceof HTMLElement ? stockStatus.dataset.sampleLabel : undefined,
          })
        : null;

      stateEstimates[stateCode] = {
        display_code: code,
        listed,
        label: stateEstimate?.rangeLabel ?? unavailableLabel,
      };
    }

    const variantFromDom = productForm?.querySelector('[ref="variantId"]');
    const variantId =
      this.#lastVariant?.id ??
      (variantFromDom instanceof HTMLInputElement ? variantFromDom.value : null);

    const atcClass =
      atcStatusKind === 'add'
        ? 'pdp-debug-panel__atc--add'
        : atcStatusKind === 'blocked'
          ? 'pdp-debug-panel__atc--blocked'
          : atcStatusKind === 'gated'
            ? 'pdp-debug-panel__atc--gated'
            : '';

    const lines = [
      ['— ATC —', ''],
      ['product_atc', productAtcDebug],
      ['product_variant_gate', productGateDebug],
      ['sample_atc', sampleAtcDebug],
      ['atc_button_disabled', atcButton instanceof HTMLButtonElement ? atcButton.disabled : null],
      ['— Current state —', ''],
      ['selected_state (session)', selectedState],
      [CART_STATE_ATTRIBUTE, this.#cartStateAttribute],
      ['selected_state_name', selectedState ? stateNames[selectedState] ?? selectedState : null],
      ['display_code', displayCode],
      ['availability_view', availabilityResult.view],
      ['purchase_mode', availability instanceof HTMLElement ? availability.dataset.purchaseMode : null],
      ['backorder_eligible', isBackorderEligible(displayCode ?? undefined, variantInventory ?? NaN)],
      ['stock_status_label', estimate?.rangeLabel ?? unavailableLabel],
      ['delivery_date', estimate?.showDeliveryDate ? estimate.deliveryDate : null],
      ['— Current variant —', ''],
      ['variant_id', variantId],
      ['variant_available', this.#lastVariant?.available ?? null],
      ['variant_inventory_quantity', variantInventory],
      ['variant_inventory_management', this.#lastVariant?.inventory_management ?? null],
      ['variant_inventory_policy', this.#lastVariant?.inventory_policy ?? null],
      ['— Product —', ''],
      ['product_id', availability instanceof HTMLElement ? availability.dataset.productId : null],
      ['state_listed_for_product', Boolean(stateListed)],
      ['delivery_estimate', estimate],
      ['— Metafields —', ''],
      ['available_states', availableStates],
      ['state_classifications', classifications],
      ['state_estimates', stateEstimates],
      ['— Meta —', ''],
      ['eta_windows_loaded', hasEtaWindows],
      ['eta_buffer_days', bufferDays],
      ['debug_sample_cart_block', isSampleCartDebugMode()],
      ['debug_url', '?debug=true'],
    ];

    const html = [
      '<p class="pdp-debug-panel__title">PDP debug</p>',
      `<p class="pdp-debug-panel__atc ${atcClass}">${atcStatusLabel}</p>`,
      ...lines.map(([key, value]) => {
        if (key.startsWith('—')) {
          return `<p class="pdp-debug-panel__section">${key.replace(/^—\s*|\s*—$/g, '')}</p>`;
        }

        return `<p class="pdp-debug-panel__row"><span class="pdp-debug-panel__key">${key}</span>: ${formatValue(value)}</p>`;
      }),
    ].join('');

    this.innerHTML = html;
  };
}

if (!customElements.get('pdp-debug-panel')) {
  customElements.define('pdp-debug-panel', PdpDebugPanel);
}

if (isDebugEnabled()) {
  const mount = () => {
    if (document.querySelector('pdp-debug-panel')) return;

    const panel = document.createElement('pdp-debug-panel');
    panel.textContent = 'PDP debug loading…';
    document.body.append(panel);
  };

  if (document.body) {
    mount();
  } else {
    document.addEventListener('DOMContentLoaded', mount, { once: true });
  }
}
